import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HandCoins, X } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  sum,
  type BillListResponse,
  type BillRowDto,
  type DeviceDto,
  type OutstandingResponse,
  type OutstandingTabDto,
} from '@gc/shared';

import { PoweredOffNote } from '@/components/PoweredOffNote';
import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import { BillDetailsDrawer } from '@/features/bills/BillDetailsDrawer';
import { unpaidPlayOnDevice } from './outstandingOnDevice';
import { VoidSessionDialog, type VoidTarget } from './VoidSessionDialog';

/**
 * "Outstanding bill" from the Actions menu (wave 3 #5): what this device is
 * still owed, one tap from the tile — unpaid PLAY that ended on the machine,
 * plus cafe and portal orders sent to it. Order rows open the existing
 * BillDetailsDrawer (imported from bills/, not copied — that drawer is another
 * agent's file).
 *
 * UNPAID PLAY IS NOT A BILL (audit, 2026-09-23). This asked for OPEN
 * RESERVATION bills, which never exist — a reservation bill is born PAID at
 * checkout — so a PC whose customer played three hours and left without paying
 * read "nothing open" here. The play comes from the Outstanding screen's own
 * list instead (`unpaidPlayOnDevice`), the one already fetched below.
 *
 * The bills list has no deviceId filter, so the orders are asked for per kind
 * with the device's name as the free-text search (it matches device name) and
 * then filtered EXACTLY on `deviceId` — "PC-1" must not surface PC-10's tabs.
 * The server's default From/To (the current business day) is kept: that is the
 * same window the Bills screen itself opens on.
 *
 * Below the device's own bills, EVERYONE outstanding (owner, 2026-08-14: "show
 * all the names of all the remaining outstanding bills people") — the same
 * party list `GET /tabs/outstanding` serves the Outstanding screen, name and
 * owed amount per row, so the press that answers "what is on this PC" also
 * answers "who still owes us" without a walk to another tab. Names only here;
 * settling stays on the Outstanding screen, one press away at the bottom.
 * A staff member whose token cannot read that list simply doesn't get the
 * section — same hiding rule as everywhere else.
 */
const OUTSTANDING_KINDS = ['CASHIER', 'PORTAL_ORDER'] as const;

export function OutstandingBillsDialog({
  device,
  currencySymbol,
  onClose,
}: {
  device: DeviceDto;
  currencySymbol: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<BillRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openBillId, setOpenBillId] = useState<string | null>(null);
  /** Null while loading or refused — either way the section stays absent. */
  const [floor, setFloor] = useState<OutstandingTabDto[] | null>(null);
  /** The floor read has answered, one way or the other; until then the device's own list is incomplete. */
  const [floorSettled, setFloorSettled] = useState(false);
  // A finished, unpaid sitting can be voided from here (`sessions.void`): this
  // list is where one is found. Bumped after a void so the list re-reads.
  const { can } = useAuth();
  const canVoid = can('sessions.void');
  const [voiding, setVoiding] = useState<VoidTarget | null>(null);
  const [floorVersion, setFloorVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get<OutstandingResponse>('/tabs/outstanding')
      .then((response) => {
        if (!cancelled) setFloor(response.tabs);
      })
      .catch(() => {
        // No permission, or a blip: the device's own orders still answer the
        // press; only its unpaid play and the everyone section are withheld.
      })
      .finally(() => {
        if (!cancelled) setFloorSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [floorVersion]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const responses = await Promise.all(
          OUTSTANDING_KINDS.map((kind) =>
            api.get<BillListResponse>(
              `/bills?kind=${kind}&status=OPEN&pageSize=200&search=${encodeURIComponent(device.name)}`,
            ),
          ),
        );
        if (cancelled) return;
        const merged = responses
          .flatMap((response) => response.rows)
          .filter((row) => row.deviceId === device.id)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        setRows(merged);
      } catch {
        if (!cancelled) setError(t('deviceBoard.outstanding.loadFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [device.id, device.name, t]);

  const money = (md: string) =>
    formatIqd(fromMillidinars(md), { currencySymbol, locale: i18n.language });

  const play = floor ? unpaidPlayOnDevice(floor, device.id) : [];
  const loading = rows === null || !floorSettled;
  const owedCount = (rows?.length ?? 0) + play.length;
  const totalMd = loading
    ? null
    : sum([...(rows ?? []), ...play].map((row) => fromMillidinars(row.totalMd)));

  return (
    <>
      <div
        className="fixed inset-0 z-40 grid place-items-center bg-black/60 p-4"
        role="dialog"
        aria-modal="true"
        aria-label={t('deviceBoard.outstanding.title', { device: device.name })}
      >
        <div className="flex max-h-full w-full max-w-md flex-col rounded-card border border-subtle bg-raised shadow-2xl">
          <header className="flex items-center gap-3 border-b border-subtle p-4">
            <h2 className="flex-1 truncate text-base font-semibold text-content">
              {t('deviceBoard.outstanding.title', { device: device.name })}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.cancel')}
              className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-auto p-3">
            {error ? (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            ) : loading ? (
              <p className="text-sm text-content-muted">{t('common.loading')}</p>
            ) : owedCount === 0 ? (
              <p className="text-sm text-content-muted">{t('deviceBoard.outstanding.none')}</p>
            ) : (
              <ul className="space-y-2">
                {play.map((row) => (
                  // No bill to open: the sitting has none until it is paid, and
                  // settling it is the Outstanding screen's job, one press below.
                  <li
                    key={row.sessionId}
                    data-unpaid-play={row.sessionId}
                    className="rounded-control border border-subtle bg-input p-2"
                  >
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-content">{row.name}</span>
                      <span className="tabular font-medium text-content">{money(row.totalMd)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-content-muted">
                      <span>
                        {t('deviceBoard.outstanding.kind.PLAY')}
                        <PoweredOffNote endedReason={row.endedReason} />
                      </span>
                      <span className="tabular">{row.duration}</span>
                    </div>
                    {canVoid ? (
                      <div className="mt-1.5 flex justify-end">
                        <button
                          type="button"
                          data-action="void-sitting"
                          onClick={() =>
                            setVoiding({
                              sessionId: row.sessionId,
                              deviceName: device.name,
                              partyName: row.name,
                              state: 'finished',
                              amountMd: row.totalMd,
                            })
                          }
                          className="rounded-full border border-danger/40 px-2.5 py-1 text-xs text-danger hover:bg-danger/10"
                        >
                          {t('sessionVoid.action')}
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
                {(rows ?? []).map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setOpenBillId(row.id)}
                      className="w-full rounded-control border border-subtle bg-input p-2 text-start transition-colors hover:border-primary"
                    >
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate text-content">
                          {row.customerName ?? row.visitorName ?? row.invoiceNumber}
                        </span>
                        <span className="tabular font-medium text-content">
                          {money(row.totalMd)}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-content-muted">
                        <span>
                          {t(`deviceBoard.outstanding.kind.${row.kind}`)}
                          {row.order ? ` · ${row.order.itemSummary}` : ''}
                        </span>
                        <span className="tabular">{row.invoiceNumber}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {floor !== null ? (
              <section aria-label={t('deviceBoard.outstanding.everyone')} className="mt-4">
                <h3 className="px-1 text-xs font-semibold uppercase tracking-[0.12em] text-content-muted">
                  {t('deviceBoard.outstanding.everyone')}
                </h3>
                {floor.length === 0 ? (
                  <p className="mt-2 px-1 text-sm text-content-muted">
                    {t('deviceBoard.outstanding.everyoneNone')}
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {floor.map((tab) => (
                      <li
                        key={tab.tabId}
                        className="flex items-center justify-between gap-2 rounded-control border border-subtle px-2 py-1.5 text-sm"
                      >
                        <span className="truncate text-content">{tab.name}</span>
                        <span className="tabular shrink-0 font-medium text-content">
                          {money(tab.totalMd)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}
          </div>

          {owedCount > 0 && totalMd !== null ? (
            <footer className="flex items-center justify-between border-t border-subtle px-4 py-3 text-sm">
              <span className="text-content-secondary">{t('common.total')}</span>
              <span className="tabular font-semibold text-content">
                {formatIqd(totalMd, { currencySymbol, locale: i18n.language })}
              </span>
            </footer>
          ) : null}

          {floor !== null && floor.length > 0 ? (
            <div className="border-t border-subtle p-3">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  void navigate('/outstanding');
                }}
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-control border border-subtle text-sm font-medium text-content transition-colors hover:bg-hover"
              >
                <HandCoins className="h-4 w-4" aria-hidden />
                {t('deviceBoard.outstanding.openScreen')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {voiding ? (
        <VoidSessionDialog
          target={voiding}
          currencySymbol={currencySymbol}
          onClose={() => setVoiding(null)}
          onDone={() => setFloorVersion((version) => version + 1)}
        />
      ) : null}

      {openBillId ? (
        <BillDetailsDrawer
          billId={openBillId}
          currencySymbol={currencySymbol}
          onClose={() => setOpenBillId(null)}
        />
      ) : null}
    </>
  );
}
